import snakeCase from "lodash/snakeCase";
import qs from "qs";
import { getAddons } from "src/common/facades/website/product/addons";
import product from "src/common/config/product";
import { findProductVariant } from "src/common/utils/product";
import { localiseUrlII } from "src/common/utils/format-localised-url";
import { getCompletedDrawersMap } from "../Canvas/helpers";

export const getDrawerId = (drawer, index) =>
  `${
    drawer.primary.type ? snakeCase(drawer.primary.type) : drawer.sliceType
  }-${index}`;

export const getGiftwrapComponents = (props) => {
  const productVariant = findProductVariant(
    props.product.variants,
    props.queryParams,
    props.model.countryModel
  );
  return productVariant.components || [];
};

export const shouldRedirectToOptions = (props) => {
  const productId = props.productId;
  const countryPrefix = props.country.prefix;
  return getAddons(productId, {
    ...props.productCustomisation,
    countryPrefix,
  })
    .then((res) => res.giftwrapPageIdx !== null || res.extrasPageIdx !== null)
    .catch(() => false);
};

/**
 * When we change the format of the book we will get all available addons for that format and replace any
 * existing addons that match with the new ones. This is because some addons are only available for some formats,
 * e.g. lmn:giftwrap (the monkey giftwrap) is only available for lmn softback.
 *
 * Note: This will be called for ANY change in the format drawer.
 *
 * @param props new props
 * @param newAddons a list of addons from eagle from the new format that was selected
 * @return {*}
 */
export const replaceAddons = (props, newAddons) => {
  const currentAddons = props.productCustomisation.components || [];
  return currentAddons.reduce((addons, currentAddon) => {
    const currentAddonArray = currentAddon.id.split(":");

    // addons with the type of `addon` will have a currentAddonArray length of 4
    // because of this, the actual type will be in the second position
    const currentAddonType =
      currentAddonArray[0] === "addon"
        ? currentAddonArray[1]
        : currentAddonArray[0];
    const currentAddonDesign = currentAddonArray.pop();

    const match = newAddons.find((newAddon) => {
      const newAddonArray = newAddon.id.split(":");
      const newAddonType =
        newAddonArray[0] === "addon" ? newAddonArray[1] : newAddonArray[0];
      const newAddonDesign = newAddonArray.pop();
      return (
        newAddonType === currentAddonType &&
        newAddonDesign === currentAddonDesign
      );
    });

    return match ? addons.concat({ id: match.id }) : addons;
  }, []);
};

export const buildUrl = (props, options = {}) => {
  const productSlug = product.getProductBySku(props.productId).slug;
  const countryModel = props.model.countryModel;
  const browserQuery = props.history.location.search;
  const search = qs.stringify(
    {
      ...props.queryParams,
      ...qs.parse(browserQuery, { ignoreQueryPrefix: true }),
    },
    { addQueryPrefix: true }
  );

  if (options.itemId) {
    return localiseUrlII(
      countryModel,
      `/personalized-products/${productSlug}/options/${options.itemId}${search}`
    );
  }

  if (options.type === "cart") {
    return localiseUrlII(countryModel, "/cart");
  }

  return localiseUrlII(
    countryModel,
    `/personalized-products/${productSlug}/options${search}`
  );
};

export const buildOrderUrl = (props) => {
  const countryModel = props.model.countryModel;
  const orderNumber = props.match.params.orderNumber;
  return localiseUrlII(countryModel, `/account/orders/${orderNumber}`);
};

export const flagUneditableDrawer = (isCartItem, hasItemId, drawers) =>
  !isCartItem && hasItemId
    ? {
        ...drawers,
        other: drawers.other.map((drawer) => {
          if (drawer.sliceType === "book_formats") {
            return {
              ...drawer,
              isHidden: true,
            };
          }
          return drawer;
        }),
      }
    : drawers;

// We only want to open the next available incomplete non optional drawer
export const openNextAvailableDrawer = (props) => {
  const options = {
    queryParams: qs.parse(props.history.location.search, {
      ignoreQueryPrefix: true,
    }),
  };

  const drawerMap = getCompletedDrawersMap({ ...props, ...options });
  const incompleteDrawers = drawerMap.incomplete;
  const optionalDrawers = drawerMap.optional;

  const incompleteNonOptionalDrawers = incompleteDrawers.filter(
    (drawer) => !optionalDrawers.includes(drawer)
  );

  const nextDrawer = incompleteNonOptionalDrawers[0];

  if (nextDrawer) {
    props.setActiveDrawer(nextDrawer);
  }
};
